<template>
  <div
    v-if="showHeader"
    class="header"
  >
    <div class="header__left-section">
      <button
        v-if="showBack && inIframe"
        title="Back to Graphics list"
        class="back-link btn btn-sm btn-icon btn-style-e margin-right-md left_header_buttons "
        data-balloon="Go back"
        data-balloon-pos="right"
        data-test="header-button-back"
        @click="back"
      >
        <span class="fa-solid fa-arrow-left" />
      </button>

      <input
        id="templateTitle"
        v-model="title"
        type="text"
        class="header__title-input"
        data-test="graphics-templateTitle"
        data-entity="graphics-templateTitle"
        @click.self="deselectElement"
      >
      <div class="header__left-helpers">
        <tags />
      </div>
      <div class="header__left-helpers">
        <button
          v-if="showShortcuts"
          class="btn btn-style-a btn-sm"
          data-balloon="Show keyboard shortcuts"
          data-balloon-pos="right"
          data-test="header-button-shortcuts"
          @click="openKeyboardShortcuts"
        >
          <span class="far fa-keyboard" />
        </button>
      </div>
    </div>
    <div class="header__center-section">
      <button
        class="btn btn-sm btn-style-b"
        :disabled="getElementGroupMap.root.length === 0"
        data-test="graphics-toggleAnimation"
        data-entity="graphics-toggleAnimation"
        @click="toggleAnimation"
      >
        <i class="far fa-play-circle" /> Animate
        {{ animationToggle ? 'out' : 'in' }}
      </button>

      <div
        v-if="showTeams && numAccounts > 1"
        class="header__controls"
      >
        <select
          v-if="accounts"
          v-model="owner"
          class="teams-list margin-left-md"
          data-test="header-select-account"
          @change="switchAccount"
        >
          <option
            v-if="plan.permissions.graphics"
            :value="userId"
          >
            Me ({{ displayName }})
          </option>
          <option
            v-for="account in accounts"
            :key="account.id"
            :value="account.id"
          >
            {{ account.displayName }}
            <template v-if="account.network != 'facebook'">
              @
            </template>{{ account.username }}
          </option>
        </select>
      </div>
    </div>

    <div
      v-if="showSave"
      class="header__controls header__right-section"
    >
      <div class="flex items-center gap-1 undo-redo">
        <button
          data-balloon="Undo"
          data-balloon-pos="down-left"
          data-test="graphics-undoButton"
          class="btn btn-sm btn-icon btn-style-b undo-redo__button"
          :disabled="$store.state.currentIndex === 0"
          @click="$store.undo"
        >
          <span class="fak fa-arrow-u-turn-up-left" />
        </button>
        <button
          data-balloon="Redo"
          data-balloon-pos="down-right"
          data-test="graphics-redoButton"
          class="btn btn-sm btn-icon btn-style-b margin-left-0 undo-redo__button"
          :disabled="$store.state.currentIndex >= $store.state.history.length - 1"
          @click="$store.redo"
        >
          <span class="fak fa-arrow-u-turn-up-right" />
        </button>
      </div>

      <template v-if="getTemplateId">
        <div class="saved-message">
          <div
            v-if="isSaving"
            class="header-unsaved-message info-box warning"
            data-test="graphics-alert-saving"
          >
            <div class="loader">
              <i class="fa-solid fa-spinner fa-spin"></i>
            </div>

            Saving
          </div>

          <div
            v-else-if="hasUnsavedChanges"
            class="header-unsaved-message info-box warning"
            data-test="graphics-alert-unsaved"
          >
            <span class="fak fa-cloud-xmark-regular margin-right-xs" />
            Unsaved changes
          </div>

          <div
            v-else-if="!hasUnsavedChanges && autoSave"
            class="header-unsaved-message info-box success"
            data-test="graphics-alert-up-to-date"
          >
            <span class="fak fa-cloud-check-regular margin-right-xs" />
            Up to date
          </div>

          <div
            v-else
            class="header-saved-message info-box success"
            data-test="graphics-alert-saved"
          >
            <span class="fak fa-cloud-check-regular margin-right-xs" />
            All changes saved
          </div>
        </div>
      </template>

      <div class="onoffswitch-container">
        <span
          id="autoSaveSwitchLabel"
          class="onoffswitch-title"
        >Autosave</span>
        <div class="onoffswitch onoffswitch-sm">
          <input
            id="autoSaveSwitch"
            v-model="autoSave"
            data-test="autoSave-checkbox"
            data-entity="autoSave-checkbox"
            type="checkbox"
            name="onoffswitch"
            class="onoffswitch-checkbox"
            checked
            aria-labelledby="autoSaveSwitchLabel"
          >
          <label
            class="onoffswitch-label"
            for="autoSaveSwitch"
            data-test="autoSave-label"
          >
            <span class="sr-only">Auto-save</span>
            <span class="onoffswitch-inner"></span>
            <span class="onoffswitch-switch"></span>
          </label>
        </div>
      </div>

      <save-template
        :has-unsaved-changes="hasUnsavedChanges"
        :auto-save-enabled="autoSave"
        @save="$emit('save')"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { postMessage, inIframe } from '../utils/helpers';

import GraphicsTags from './GraphicsTags.vue';
import SaveTemplate from './SaveTemplate.vue';

export default {
  emits: ['save', 'enable-auto-save', 'auto-save', 'account', 'modal'],
  components: {
    Tags: GraphicsTags,
    SaveTemplate,
  },

  props: {
    showBack: { type: Boolean, defualt: false },
    showTeams: { type: Boolean, defualt: false },
    showSave: { type: Boolean, default: false },
    showShortcuts: { type: Boolean, default: false },
    hasUnsavedChanges: { type: Boolean, default: false },
    disableAutoSave: { type: Boolean, default: false },
  },

  data() {
    return {
      owner: this.$store.state.user.owner,
      autoSaveTimeout: null,
      autoSave: false,
      inIframe: inIframe(),
    };
  },

  computed: {
    ...mapGetters('graphicsTemplate', [
      'getTemplateId',
      'getTemplateTitle',
      'getElementGroupMap',
    ]),

    ...mapState({
      isSaving: (state) => state.templates.isSaving,
      graphicTemplateId: (state) => state.graphicsTemplate._id,
    }),

    showHeader() {
      return (
        this.showBack
        || this.showShortcuts
        || (this.showTeams && this.accounts && this.accounts.length)
      );
    },

    userId() {
      return this.$store.state.user.user.id;
    },

    displayName() {
      return this.$store.state.user.user.profile.displayName;
    },

    plan() {
      return this.$store.state.user.plan;
    },

    /**
    * Total number of accounts the current user has access to that also has access to graphics.
    */
    numAccounts() {
      return this.accounts.length + (this.plan.permissions.graphics ? 1 : 0);
    },

    accounts() {
      const { accounts } = this.$store.state.user;

      if (!accounts) {
        return [];
      }

      return Object.keys(accounts.owners).reduce((memo, key) => {
        const account = accounts.owners[key];

        return memo.concat([{
          id: key,
          displayName: account.displayName,
          network: account.network,
          username: account.username,
        }]);
      }, []);
    },

    title: {
      get() {
        return this.getTemplateTitle;
      },
      set(value) {
        this.setTemplateTitle({
          value,
        });
      },
    },

    toggleLooping: {
      get() {
        return this.$store.state.templates.runLoopAnimations;
      },
      set(value) {
        this.$store.commit('TOGGLE_LOOPING_ANIMATIONS', value);
      },
    },

    animationToggle() {
      return this.$store.state.templates.animationToggle;
    },
  },
  watch: {
    autoSave(v) {
      // while autoSave is true, save the template every 10 seconds
      if (v) {
        this.autoSaveOn();
        this.setAutoSave(true);
      } else {
        this.autoSaveOff();
        this.setAutoSave(false);
      }
    },
    disableAutoSave(v) {
      if (v) {
        this.autoSave = false;
      }
    },
  },
  async mounted() {
    this.autoSave = await this.getAutoSave();
    if (this.autoSave) {
      this.autoSaveOn();
    }
  },
  methods: {
    ...mapActions('graphicsTemplate', ['setTemplateTitle']),

    getAutoSave() {
      const storedUserSettingsJson = sessionStorage.getItem(`graphics_templateAutoSave_${this.userId}`);
      const storedUserSettings = JSON.parse(storedUserSettingsJson);

      if (storedUserSettings && storedUserSettings.includes(this.graphicTemplateId)) {
        return true;
      }

      return false;
    },

    setAutoSave(value) {
      const storedUserSettingsJson = sessionStorage.getItem(`graphics_templateAutoSave_${this.userId}`);
      let storedUserSettings = JSON.parse(storedUserSettingsJson);

      if (!storedUserSettings) {
        storedUserSettings = [];
      }

      if (!value) {
        const index = storedUserSettings.indexOf(this.graphicTemplateId);
        if (index > -1) {
          storedUserSettings.splice(index, 1);
        }
      } else if (!storedUserSettings.includes(this.graphicTemplateId)) {
        storedUserSettings.push(this.graphicTemplateId);
      }

      sessionStorage.setItem(`graphics_templateAutoSave_${this.userId}`, JSON.stringify(storedUserSettings));
    },

    autoSaveOn() {
      this.$nextTick(() => {
        this.$emit('enable-auto-save');
        if (this.hasUnsavedChanges) {
          this.$emit('auto-save');
        }
      });

      this.autoSaveTimeout = setInterval(() => {
        if (this.hasUnsavedChanges) {
          this.$emit('auto-save');
        }
      }, 10000);
    },

    autoSaveOff() {
      clearInterval(this.autoSaveTimeout);
    },

    back() {
      postMessage('back');
    },

    switchAccount() {
      this.$store.commit('SET_OWNER', this.owner);
      this.$emit('account', this.owner);
    },

    openKeyboardShortcuts() {
      this.$emit('modal', true);
    },

    deselectElement() {
      this.$store.dispatch('setTextEditing', false);
      this.$store.dispatch('selectElements', []);
    },

    toggleAnimation() {
      this.$store.dispatch('toggleAnimation');
    },
  },
};
</script>

<style lang="scss" scoped>
.left_header_buttons {
  padding: 0;
  font-size: 20px;
  color: #2bacca;
}

.saved-message,
.onoffswitch-container {
  display: flex;
  justify-content: center;
}
.saved-message {
  width: 165px;
}
.onoffswitch-container {
  width: 160px;
}
.undo-redo{
  padding: 0 15px;

  &__button {
    background: transparent;
    border: none;
    font-size: 18px;
    &:disabled,
    &:disabled:hover {
      color: grey;
      background: transparent;
    }
    &:not(:disabled):hover,
    &:not(:disabled):focus {
      background: transparent;
      color: #2bacca;
    }
  }
}
.header {
  &__left-section {
    display: flex;
    align-items: center;
  }

  &__left-helpers {
    border-left: 1px solid #dfeaef;
    padding: 0 15px;
  }

  &__center-section {
    display: flex;
    justify-content: center;
    flex-grow: 1;
    gap: 8px;

    .checkbox {
      display: flex;
      align-items: center;
    }
  }

  &__right-section {
    padding: 0 15px;
  }

  &__title-input {
    padding: 6px;
    margin-right: 15px;
  }
}
</style>
